/**
 * AnalyticsPage
 *
 * Main analytics dashboard page displaying revenue, clients, bookings, and time tracking metrics.
 * Available for Rise and Summit subscription tiers.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Skeleton,
  Link,
  Card,
  CardContent,
} from '@mui/material';
import { FileDownload, TrendingUp, Check } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { analyticsApi, DateRangeType, downloadBlob } from '../services/analyticsApi';
import RevenueSection from '../components/analytics/RevenueSection';
import ClientsSection from '../components/analytics/ClientsSection';
import BookingsSection from '../components/analytics/BookingsSection';
import TimeSection from '../components/analytics/TimeSection';
import { AnalyticsPageSkeleton } from '../components/analytics/AnalyticsSkeleton';
import { AnalyticsErrorBoundary } from '../components/analytics/AnalyticsErrorBoundary';
import { ANALYTICS_SPACING } from '../components/analytics/constants';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '@elevair/shared';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const [dateRange, setDateRange] = useState<DateRangeType>('month');
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  // Check tier access - Rise and Summit can access analytics
  let tier = organization?.subscription?.tier || 'free';
  // Fallback: If tier is 'free' but status indicates trial, treat as trial
  if (tier === 'free' && (organization?.subscription?.status === 'trialing' || organization?.subscription?.status === 'trial')) {
    tier = 'trial';
  }
  // Fallback: If tier is 'free' but trialEndsAt is in the future, treat as trial
  if (tier === 'free' && organization?.subscription?.trialEndsAt && new Date(organization.subscription.trialEndsAt) > new Date()) {
    tier = 'trial';
  }

  const hasAnalyticsAccess = tier === 'rise' || tier === 'summit';

  // Feature highlights for upgrade prompt
  const featureHighlights = [
    'Revenue tracking & trends',
    'Client growth over time',
    'Invoice & payment insights',
    'Booking & utilization rates',
    'Exportable reports',
  ];

  // Show upgrade prompt for Trial and Launch tiers - don't make any API calls
  if (!hasAnalyticsAccess) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight={600}>
            Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track your business performance and growth
          </Typography>
        </Box>

        {/* Upgrade Prompt Card */}
        <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: `${COLORS.accent}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <TrendingUp sx={{ fontSize: 40, color: COLORS.accent }} />
            </Box>

            <Typography variant="h5" fontWeight={600} gutterBottom>
              Unlock Business Analytics
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              See the full picture of your business performance.
            </Typography>

            {/* Feature Highlights */}
            <Box sx={{ textAlign: 'left', maxWidth: 300, mx: 'auto', mb: 3 }}>
              {featureHighlights.map((feature) => (
                <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Check sx={{ fontSize: 20, color: COLORS.accent }} />
                  <Typography variant="body2">{feature}</Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Elevair Analytics gives you real-time visibility into what's working,
              what's growing, and where to focus next. Stop guessing — start making
              data-driven decisions.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/settings/billing')}
              sx={{
                backgroundColor: COLORS.accent,
                '&:hover': { backgroundColor: COLORS.accent, opacity: 0.9 },
                mb: 1.5,
              }}
            >
              Upgrade to Rise
            </Button>

            <Typography variant="caption" color="text.secondary" display="block">
              Available on Rise and Summit plans
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Fetch revenue analytics data - only for eligible tiers
  const {
    data: revenueData,
    isLoading: revenueLoading,
    error: revenueError,
  } = useQuery({
    queryKey: ['analytics', 'revenue', dateRange],
    queryFn: () => analyticsApi.getRevenue({ range: dateRange }),
  });

  // Fetch client analytics data
  const {
    data: clientData,
    isLoading: clientLoading,
    error: clientError,
  } = useQuery({
    queryKey: ['analytics', 'clients', dateRange],
    queryFn: () => analyticsApi.getClients({ range: dateRange }),
  });

  // Fetch booking analytics data
  const {
    data: bookingData,
    isLoading: bookingLoading,
    error: bookingError,
  } = useQuery({
    queryKey: ['analytics', 'bookings', dateRange],
    queryFn: () => analyticsApi.getBookings({ range: dateRange }),
  });

  // Fetch time tracking analytics data
  const {
    data: timeData,
    isLoading: timeLoading,
    error: timeError,
  } = useQuery({
    queryKey: ['analytics', 'time', dateRange],
    queryFn: () => analyticsApi.getTime({ range: dateRange }),
  });

  // Initial loading state (no data yet)
  const isInitialLoading =
    revenueLoading &&
    !revenueData &&
    clientLoading &&
    !clientData &&
    bookingLoading &&
    !bookingData &&
    timeLoading &&
    !timeData;

  // Combined error state
  const error = revenueError || clientError || bookingError || timeError;

  // Handle date range change
  const handleDateRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newRange: DateRangeType | null
  ) => {
    if (newRange !== null) {
      setDateRange(newRange);
    }
  };

  // Export menu handlers
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Handle export
  const handleExport = async (format: 'csv' | 'pdf') => {
    handleExportClose();
    setExporting(true);

    try {
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `elevair-analytics-${dateRange}-${dateStr}.${format}`;

      if (format === 'csv') {
        const blob = await analyticsApi.exportCsv({ range: dateRange });
        downloadBlob(blob, filename);
      } else {
        const blob = await analyticsApi.exportPdf({ range: dateRange });
        downloadBlob(blob, filename);
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // Show full page skeleton on initial load
  if (isInitialLoading) {
    return (
      <Box
        sx={{
          p: 3,
          '@media print': {
            '& .no-print': { display: 'none' },
          },
        }}
      >
        {/* Page Header Skeleton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Track your business performance and growth
            </Typography>
          </Box>
          <Skeleton width={300} height={40} className="no-print" />
        </Box>
        <AnalyticsPageSkeleton />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        // Print styles
        '@media print': {
          '& .MuiPaper-root': {
            boxShadow: 'none',
            border: '1px solid #ddd',
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
          },
          '& .no-print': {
            display: 'none',
          },
          '& .MuiButton-root': {
            display: 'none',
          },
        },
      }}
    >
      {/* Skip links for keyboard users */}
      <Box
        component="nav"
        aria-label="Skip to section"
        sx={{
          position: 'absolute',
          left: '-9999px',
          '&:focus-within': {
            position: 'static',
            left: 'auto',
            mb: 2,
          },
        }}
      >
        <Link href="#revenue-section" sx={{ mr: 1 }}>
          Skip to Revenue
        </Link>
        <Link href="#clients-section" sx={{ mr: 1 }}>
          Skip to Clients
        </Link>
        <Link href="#bookings-section" sx={{ mr: 1 }}>
          Skip to Bookings
        </Link>
        <Link href="#time-section">Skip to Time</Link>
      </Box>

      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track your business performance and growth
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} className="no-print">
          {/* Date Range Toggle */}
          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={handleDateRangeChange}
            size="small"
            aria-label="Date range filter"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 500,
              },
              '& .Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              },
            }}
          >
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="quarter">Quarter</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>

          {/* Export Button */}
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <FileDownload />}
            onClick={handleExportClick}
            disabled={exporting}
            sx={{ textTransform: 'none' }}
            aria-label="Export analytics data"
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
            <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Date Range Label */}
      {revenueData?.dateRange && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {revenueData.dateRange.label}
        </Typography>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error instanceof Error
            ? error.message
            : 'Failed to load analytics data. Please try again.'}
        </Alert>
      )}

      {/* Analytics Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: ANALYTICS_SPACING.sectionGap }}>
        {/* Revenue Section */}
        <Box id="revenue-section" tabIndex={-1}>
          <AnalyticsErrorBoundary fallbackTitle="Revenue analytics failed to load">
            <RevenueSection data={revenueData || null} loading={revenueLoading} />
          </AnalyticsErrorBoundary>
        </Box>

        {/* Clients Section */}
        <Box id="clients-section" tabIndex={-1}>
          <AnalyticsErrorBoundary fallbackTitle="Client analytics failed to load">
            <ClientsSection data={clientData || null} loading={clientLoading} />
          </AnalyticsErrorBoundary>
        </Box>

        {/* Bookings Section */}
        <Box id="bookings-section" tabIndex={-1}>
          <AnalyticsErrorBoundary fallbackTitle="Booking analytics failed to load">
            <BookingsSection data={bookingData || null} loading={bookingLoading} />
          </AnalyticsErrorBoundary>
        </Box>

        {/* Time Tracking Section */}
        <Box id="time-section" tabIndex={-1}>
          <AnalyticsErrorBoundary fallbackTitle="Time analytics failed to load">
            <TimeSection data={timeData || null} loading={timeLoading} />
          </AnalyticsErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}
